<template>
  <b-modal
    :visible="value"
    :title="entityKind === 'artist' ? 'Выбор артиста' : 'Выбор фильма'"
    size="lg"
    hide-footer
    @change="$emit('input', $event)"
    @shown="onShown"
  >
    <div class="d-flex flex-wrap align-items-end mb-1" style="gap: 8px">
      <b-form-group label="Название" class="mb-0 flex-grow-1">
        <b-form-input v-model="filters.q" placeholder="Поиск по названию" @keyup.enter="fetchPage(1)" />
      </b-form-group>

      <template v-if="entityKind === 'artist'">
        <b-form-group label="Ру" class="mb-0">
          <b-form-select v-model="filters.russian" :options="russianOptions" style="width: 90px" />
        </b-form-group>
        <b-form-group label="Сложность от/до" class="mb-0">
          <div class="d-flex" style="gap: 4px">
            <b-form-input v-model="filters.difficulty_from" type="number" step="0.1" style="width: 70px" />
            <b-form-input v-model="filters.difficulty_to" type="number" step="0.1" style="width: 70px" />
          </div>
        </b-form-group>
        <b-form-group label="Прослушивания от" class="mb-0">
          <b-form-input v-model="filters.listeners_from" type="number" style="width: 120px" />
        </b-form-group>
      </template>

      <template v-else>
        <b-form-group label="Год от/до" class="mb-0">
          <div class="d-flex" style="gap: 4px">
            <b-form-input v-model="filters.year_from" type="number" style="width: 80px" />
            <b-form-input v-model="filters.year_to" type="number" style="width: 80px" />
          </div>
        </b-form-group>
        <b-form-group label="Рейтинг КП от" class="mb-0">
          <b-form-input v-model="filters.rating_from" type="number" step="0.1" style="width: 80px" />
        </b-form-group>
        <b-form-group label="Жанр" class="mb-0">
          <b-form-input v-model="filters.genre" style="width: 120px" />
        </b-form-group>
      </template>

      <b-button variant="primary" :disabled="isLoading" @click="fetchPage(1)">
        Найти
      </b-button>
    </div>

    <b-form-checkbox
      v-if="hasAutotopic"
      v-model="filters.in_topic"
      class="mb-1"
      @change="fetchPage(1)"
    >
      Только из темы «{{ currentRt.autotopic_title || currentRt.topic_name }}»
    </b-form-checkbox>

    <b-overlay :show="isLoading" rounded="sm">
      <div v-if="items.length">
        <div
          v-for="item in items"
          :key="item.id"
          class="d-flex align-items-center justify-content-between candidate-row"
        >
          <div class="d-flex align-items-center" style="gap: 8px; min-width: 0">
            <img
              v-if="entityKind !== 'artist' && item.poster_preview"
              :src="item.poster_preview"
              alt="Постер"
              class="candidate-poster"
            >
            <div style="min-width: 0">
              <div class="text-truncate">
                <strong>{{ entityKind === 'artist' ? item.title : item.title_ru }}</strong>
                <span v-if="entityKind !== 'artist' && item.title_en" class="text-muted"> / {{ item.title_en }}</span>
                <span v-if="item.year" class="text-muted"> ({{ item.year }})</span>
              </div>
              <div class="d-flex flex-wrap" style="gap: 4px">
                <b-badge v-if="entityKind === 'artist' && item.russian" variant="light-info">Ру</b-badge>
                <b-badge v-if="entityKind === 'artist' && item.difficulty" variant="light-secondary">
                  сложность {{ item.difficulty }}
                </b-badge>
                <b-badge v-if="entityKind === 'artist' && item.spotify_playcount" variant="light-secondary">
                  {{ item.spotify_playcount }} прослушиваний
                </b-badge>
                <b-badge v-if="entityKind !== 'artist' && item.rating_kp" variant="light-secondary">
                  КП {{ item.rating_kp }}
                </b-badge>
                <b-badge v-if="pickFor !== 'distractor'" variant="light-primary">{{ item.items_count }} {{ countLabel }}</b-badge>
                <b-badge v-if="item.used" variant="light-danger">уже в игре</b-badge>
              </div>
            </div>
          </div>
          <b-button
            variant="outline-success"
            size="sm"
            :disabled="item.used && pickFor !== 'distractor'"
            @click="$emit('picked', { id: item.id, title: entityKind === 'artist' ? item.title : item.title_ru }); $emit('input', false)"
          >
            Выбрать
          </b-button>
        </div>

        <b-pagination
          v-if="total > perPage"
          :value="page"
          :total-rows="total"
          :per-page="perPage"
          class="mt-1 mb-0"
          @change="fetchPage($event)"
        />
      </div>
      <p v-else-if="!isLoading && searched" class="text-muted my-2">
        Ничего не найдено{{ filters.in_topic && hasAutotopic ? ' — попробуйте снять галочку «Только из темы»' : '' }}
      </p>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BOverlay, BBadge, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BPagination,
} from 'bootstrap-vue'
import { ref, reactive, computed } from '@vue/composition-api'
import store from '@/store'
import { itemsCountLabel } from './gameBuilderMusicFormats'

export default {
  components: {
    BModal, BButton, BOverlay, BBadge, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BPagination,
  },
  props: {
    value: { type: Boolean, default: false },
    gameId: { type: [Number, String], required: true },
    rtId: { type: [Number, String], required: true },
    currentRt: { type: Object, required: true },
    entityKind: { type: String, default: 'artist' }, // 'artist' | 'movie'
    side: { type: String, default: null }, // 'audio' | 'image' | null — для парных форматов
    pickFor: { type: String, default: 'question' }, // 'question' | 'distractor'
  },
  setup(props) {
    const items = ref([])
    const total = ref(0)
    const page = ref(1)
    const perPage = 10
    const isLoading = ref(false)
    const searched = ref(false)

    const hasAutotopic = computed(() => !!props.currentRt.autotopic_id)

    const filters = reactive({
      q: '',
      in_topic: true,
      russian: null,
      difficulty_from: null,
      difficulty_to: null,
      listeners_from: null,
      year_from: null,
      year_to: null,
      rating_from: null,
      genre: '',
    })

    const russianOptions = [
      { value: null, text: 'Все' },
      { value: true, text: 'Да' },
      { value: false, text: 'Нет' },
    ]

    const countLabel = computed(() => (props.entityKind === 'artist'
      ? itemsCountLabel(props.currentRt.question_format || 'music', props.side)
      : 'кадров'))

    const fetchPage = pageNumber => {
      isLoading.value = true
      const params = { page: pageNumber }
      Object.entries(filters).forEach(([key, filterValue]) => {
        if (filterValue !== null && filterValue !== '') params[key] = filterValue
      })
      if (!hasAutotopic.value) delete params.in_topic
      if (props.side) params.side = props.side
      // Режим выбора дистрактора
      if (props.pickFor === 'distractor') params.for = 'distractor'

      store.dispatch('game-builder/searchCandidates', { gameId: props.gameId, rtId: props.rtId, params })
        .then(response => {
          const data = response.data.data || response.data
          items.value = data.items || []
          total.value = data.total || 0
          page.value = data.page || pageNumber
          searched.value = true
          isLoading.value = false
        })
        .catch(() => { isLoading.value = false })
    }

    const onShown = () => {
      // Свежий поиск при каждом открытии, фильтры между открытиями не сохраняем
      filters.q = ''
      filters.in_topic = true
      filters.russian = null
      filters.difficulty_from = null
      filters.difficulty_to = null
      filters.listeners_from = null
      filters.year_from = null
      filters.year_to = null
      filters.rating_from = null
      filters.genre = ''
      searched.value = false
      items.value = []
      fetchPage(1)
    }

    return {
      items, total, page, perPage, isLoading, searched, hasAutotopic, filters, russianOptions, countLabel, fetchPage, onShown,
    }
  },
}
</script>

<style scoped>
.candidate-row {
  padding: 6px 8px;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  margin-bottom: 4px;
}
.candidate-poster {
  width: 40px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
